// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.
import { Component } from '@angular/core';
import { D3eService } from '../../../services/d3e-service';
import { SelectedOrgMethod } from '../../../shared/selectedOrg';
import { ISoftwareVersion, ISubscription, StripeProductTier } from '../../../types/subscription';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { HeaderService } from '../../header.service';
import multiDownload from 'multi-download';
import { Actions, ActionService } from '../../../services/action-service';
import { ConfigurationService } from '../../../services/configuration-service';
import {
    isFullDriveEncryptionSub,
    isHardwareSub,
    isSubscriptionExpired,
    sortSubscriptionsByPriority
} from '../../../shared/subscription.methods';
import { OrgType } from '../../../types/group';
import { UserService } from '../../../services/user-service';
import {ActivatedRoute} from '@angular/router';
import { ClipboardService } from '../../../services/clipboard.service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-downloads',
    templateUrl: './downloads.component.html',
    styleUrls: ['./downloads.component.scss'],
    standalone: false
})
export class DownloadsComponent {
  isMSP$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loadingSubscriptions$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  freshDeskCliGuideUrl: string;
  freshDeskMsiGuideUrl: string;
  freshDeskPbaGuideUrl: string;
  freshDeskFdeGuideUrl: string;
  freshDeskAgentGuideUrl: string;
  subscriptions$: Observable<ISubscription[]>;
  softwareVersion$: Observable<ISoftwareVersion>;
  selectedSubscription$: BehaviorSubject<ISubscription> = new BehaviorSubject<ISubscription>(null);
  installCommand = '/qn ACCEPT_EULA=Y';
  activationCommand = 'ACTIVATION_CODE=';
  protected readonly StripeProductTier = StripeProductTier;
  pbaEnabled = true;
  fdeEnabled = true;

  constructor(private d3eService: D3eService,
              private headerService: HeaderService,
              private translate: TranslateService,
              private actionService: ActionService,
              public configurationService: ConfigurationService,
              private readonly activatedRoute: ActivatedRoute,
              private readonly clipboard: ClipboardService,
              private userService: UserService) {
    this.headerService.setContextHelp([
      'help.agent-install',
      'help.pba-install',
      'help.fde-install',
      'help.cli-install',
      'help.msi-install'
    ]);
    this.headerService.setOrgSelectorDefaultOption(this.translate.instant('SELECTOR_YOUR_DOWNLOADS'));
    this.headerService.setTitle(translate.instant('downloads.page-title'));
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-download-multiple');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(true);

    const contextHelpMap = this.configurationService.contextHelpMap;
    this.freshDeskMsiGuideUrl = contextHelpMap['help.msi-install'];
    this.freshDeskCliGuideUrl = contextHelpMap['help.cli-install'];
    this.freshDeskPbaGuideUrl = contextHelpMap['help.pba-install'];
    this.freshDeskFdeGuideUrl = contextHelpMap['help.fde-install'];
    this.freshDeskAgentGuideUrl = contextHelpMap['help.agent-install'];

    combineLatest([
      this.headerService.orgSelectedChanged,
      this.userService.subscribeToUser(),
    ]).pipe(
      tap(() => this.loadingSubscriptions$.next(false)),
      switchMap(([selectedOrgId, user]) => {
        if (user?.org_type.value === OrgType.MSP && selectedOrgId === 'All') {
          this.isMSP$.next(true);
          return EMPTY;
        }
        this.isMSP$.next(false);
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
        return combineLatest([
          this.d3eService.get_subscriptions(selectedOrg),
          this.activatedRoute.queryParamMap,
        ]);
      }),
      map(([subscriptions, paramMap]) => {
        const disabledSubs: ISubscription[] = subscriptions.filter(s => this.disabledSubscription(s));
        disabledSubs.sort(sortSubscriptionsByPriority);

        const goodSubs: ISubscription[] = subscriptions.filter(s => !this.disabledSubscription(s));
        goodSubs.sort(sortSubscriptionsByPriority);
        const allSubs = [...goodSubs, ...disabledSubs];
        const selectedSubId = paramMap.get("subscriptionId");
        if (selectedSubId) {
          const selectedSub = subscriptions.find(s => s.subscription_id === selectedSubId);
          this.onSubscriptionSelectionChange(selectedSub);
        } else if (allSubs.length > 0) {
          this.onSubscriptionSelectionChange(goodSubs[0])
        }
        this.subscriptions$ = of(allSubs);
        return of(allSubs);
      }),
      untilDestroyed(this)
    ).subscribe(() => this.loadingSubscriptions$.next(true));

    this.softwareVersion$ = this.selectedSubscription$
      .pipe(
        switchMap(subscription => {
          if (!subscription) {
            return EMPTY;
          }
          return this.d3eService.get_d3e_software_version(subscription.uuid);
        })
    );

  }

  hasAvailableLicenses(subscription: ISubscription) {
    return subscription != null && (subscription.count - subscription.used) > 0;
  }

  disabledSubscription(subscription: ISubscription) : boolean {
    if (!subscription || !this.hasAvailableLicenses(subscription)) {
      return true;
    }
    return isSubscriptionExpired(subscription);
  }

  isSubExpired(subscription: ISubscription) : boolean {
    return isSubscriptionExpired(subscription);
  }

  onSubscriptionSelectionChange(subscription: ISubscription) {
    this.selectedSubscription$.next(subscription);
  }

  // Download with a unique filename based on UUID
  onDownloadD3E(subscription: ISubscription, softwareVersion: ISoftwareVersion) {
    let installerName = softwareVersion.path.trim().split('/').pop();
    if (!isHardwareSub(subscription) && !isFullDriveEncryptionSub(subscription)) {
        const subscriptionFilename = this.getUuidFilename(subscription, softwareVersion);
        installerName = subscriptionFilename.trim().split('/').pop();
    }
    // Always use the current browser origin to download the installer file.
    const origin = document.location.origin;
    const softwareUrl = new URL(softwareVersion.path);
    const path = new URL(softwareUrl.pathname, origin);
    multiDownload([path.href], {
      rename: () => installerName
    });
    this.softwareDownloadNotification(subscription, softwareVersion.path, 'download');
  }

  onDownloadAsset(subscription: ISubscription, downloadUrl: string, action: Actions) {
    const installerName = downloadUrl.trim().split('/').pop();
    // Always use the current browser origin to download the installer file.
    const origin = document.location.origin;
    const url = new URL(downloadUrl);
    const path = new URL(url.pathname, origin);
    multiDownload([path.href], {
      rename: () => installerName
    });
    this.softwareDownloadNotification(subscription, downloadUrl, action);
  }

  transformInstallerUrl(urlStr: string, suffix: string): string {
    try {
      const url = new URL(urlStr);
      const fullFilename = url.pathname.split('/').pop() || '';
      const extensionRegex = /(\.(?:exe|msi))$/i;
      // '$1' captures and reinstates the original extension (.exe or .msi)
      return fullFilename.replace(extensionRegex, `${suffix}$1`);

    } catch (error) {
      console.error("Invalid URL provided", error);
      return '';
    }
  }

  onDownloadAgent(subscription: ISubscription, downloadUrl: string, softwareVersion: ISoftwareVersion, action: Actions) {
    let installerName = downloadUrl.trim().split('/').pop();
    // Always use the current browser origin to download the installer file.
    const origin = document.location.origin;
    const url = new URL(downloadUrl);
    const path = new URL(url.pathname, origin);
    installerName = this.transformInstallerUrl(downloadUrl, `_${softwareVersion.activation_code}`);

    multiDownload([path.href], {
      rename: () => installerName
    });
    this.softwareDownloadNotification(subscription, downloadUrl, action);
  }

  onCopyActivationCode(softwareVersion: ISoftwareVersion, subscription: ISubscription) {
    // Note: softwareVersion.activation_code would be more robust, if we knew the softwareVersion was up-to-date.
    // If it isn't, the feature_flags might be incorrect.
    const copyText = `${softwareVersion.feature_flags}_${subscription.uuid}_${softwareVersion.system}`;
    this.clipboard.copyMessage(copyText, this.translate.instant('DA_ACTIVATION_SNACKBAR')).then(null);
    this.actionService.postDownload(copyText, 'copy_activation', null, subscription.uuid);
  }

  onCopyWinFdeInstallerCommand(subscription: ISubscription, msiUrl: string) {
    const copyText = this.getWinFdeInstallerCommand(msiUrl);
    this.clipboard.copyMessage(copyText, this.translate.instant('downloads.fde-install-cmd-copied')).then(null);
    this.actionService.postDownload(copyText, 'copy_msi_command', null, subscription.uuid);
  }

  onCopyAgentActivationCode(softwareVersion: ISoftwareVersion, msi_url: string, subscriptionUUID: string) {
    const latestInstallerName = msi_url.trim().split('/').pop();
    const parts = [
      latestInstallerName,
      `ACTIVATION_CODE=${softwareVersion.feature_flags}_${subscriptionUUID}_${softwareVersion.system}`,
      this.pbaParameters,
      this.fdeParameters,
      '/qn',
      '/l*V install.log'
    ];

    const copyText = parts
      .filter(part => part?.trim())
      .join(' ');

    this.clipboard.copyMessage(copyText, this.translate.instant('downloads.agent-install-cmd-copied')).then(null);
    this.actionService.postDownload(copyText, 'copy_msi_command', null, subscriptionUUID);
  }

  onCopyShareableUrl(subscription: ISubscription, softwareVersion: ISoftwareVersion) {
    const copyText = this.getDownloadURL(subscription, softwareVersion);
    this.clipboard.copyMessage(copyText, this.translate.instant('DA_URL_SNACKBAR')).then(null);
    this.actionService.postDownload(copyText, 'copy_url', null, subscription.uuid);
  }

  onCopyShareableAgentUrl(subscription: ISubscription, softwareVersion: ISoftwareVersion) {
    const copyText = this.getAgentDownloadURL(subscription, softwareVersion);
    this.clipboard.copyMessage(copyText, this.translate.instant('DA_URL_SNACKBAR')).then(null);
    this.actionService.postDownload(copyText, 'copy_url', null, subscription.uuid);
  }

  onCopyMsiInstallCommand(subscription: ISubscription, softwareVersion: ISoftwareVersion) {
    const msi = softwareVersion.msi_url.trim().split('/').pop();
    let copyText: string = `${msi} ${this.installCommand}`;
    if (!isHardwareSub(subscription) && !isFullDriveEncryptionSub(subscription)) {
        copyText = `${msi} ${this.installCommand} ${this.activationCommand}${softwareVersion.feature_flags}_${subscription.uuid}_${softwareVersion.system}`;
    }
    this.clipboard.copyMessage(copyText, this.translate.instant('downloads.data-defense-install-cmd-copied')).then(null);
    this.actionService.postDownload(copyText, 'copy_msi_command', null, subscription.uuid);
  }

  getUuidFilename(subscription: ISubscription, softwareVersion: ISoftwareVersion): string {
    let subscriptionFilename = '';
    const uuid = subscription.uuid;

    if (!softwareVersion) {
      return subscriptionFilename;
    }

    if (softwareVersion?.subscription_path) {
        subscriptionFilename = softwareVersion.subscription_path;
    } else {
        if (softwareVersion?.path && softwareVersion?.system && uuid) {
            const installerName = softwareVersion.path.trim().split('/').pop();
            const installerType = installerName.split('.').pop();
            let installerBody = softwareVersion.path.substr(0, softwareVersion.path.lastIndexOf('.' + installerType));
            // If the installerBody has a trailing underscore, then remove it,
            // since we will add one to it in the next step.
            if (installerBody.charAt(installerBody.length - 1) === '_') {
                installerBody = installerBody.substr(0, installerBody.length - 1);
            }
            // If softwareVersions is up-to-date with selected subscription, then softwareVersion.activation_code
            // would be better, as feature_flags is dependant upon the subscription as well (specific feature mask bits, and partner_id)
            subscriptionFilename = installerBody + '_' + softwareVersion.feature_flags + '_'
                + uuid + '_' + softwareVersion.system + '.' + installerType;
        }
    }

    return subscriptionFilename;
  }

  getDownloadURL(subscription: ISubscription, softwareVersion: ISoftwareVersion): string {
    const url = new URL(this.getUuidFilename(subscription, softwareVersion));
    return `${url.protocol}//${url.host}/download/${subscription.uuid}`;
  }

  getAgentDownloadURL(subscription: ISubscription, softwareVersion: ISoftwareVersion): string {
    const url = new URL(this.getUuidFilename(subscription, softwareVersion));
    return `${url.protocol}//${url.host}/agent-download/${subscription.uuid}`;
  }

  softwareDownloadNotification(subscription: ISubscription, filename: string, action: Actions) {
      this.actionService.postDownload(filename, action, null, subscription.uuid);
  }

  getWinFdeInstallerCommand(msiUrl: string): string {
      const parts = [
        msiUrl.split('/').pop(),
        '/qn',
        'USER_NAME=<username>',
        'USER_CRED=<password>',
        'ACCEPT_EULA=Y',
        'DATABACKEDUP=Y'
      ];

      return parts
        .filter(part => part?.trim())
        .join(' ');
  }

  get pbaParameters(): string {
      return this.pbaEnabled
        ? 'PBA_USER_NAME=<username> PBA_USER_PASSWORD=<password> PBA_DRIVE=<path-or-serial>'
        : '';
  }

  get fdeParameters(): string {
      return this.fdeEnabled
        ? 'FDE_USER_NAME=<username> FDE_USER_PASSWORD=<password>'
        : '';
  }

  getAgentInstallerCommand(softwareVersion: ISoftwareVersion, msi_url: string, subscriptionUUID: string) {
      const latestInstallerName = msi_url.trim().split('/').pop();
      const parts = [
        latestInstallerName,
        `ACTIVATION_CODE=${softwareVersion.feature_flags}_${subscriptionUUID}_${softwareVersion.system}`,
        this.pbaParameters,
        this.fdeParameters,
        '/qn',
        '/l*V install.log'
      ];

      return parts
        .filter(part => part?.trim())
        .join(' ');
  }

}
